/**
 * WearablesCaptureImporter — Import Vuzix glass captures into Document Studio
 *
 * Displays recent WearableCapture entities (photos, voice notes, barcode scans)
 * from the current actor's sessions. Clinicians capture evidence during rounds
 * via Vuzix Blade 2 / Shield — this component bridges those captures into the
 * Document Studio chaos pipeline.
 *
 * State machine:
 *   LOADING → QUERY_ERROR | EMPTY_STATE | BROWSING
 *   BROWSING → [select] → BROWSING (tracked selection)
 *            → [Import Selected] → IMPORTING (per-capture progress)
 *              → IMPORTED | PARTIAL_SUCCESS | IMPORT_ERROR
 *
 * DRR-105: actorId from useAuth().user.actorGlobalId (sequential format "17_2")
 * DRR-128: no duplication of S3 data — backend registers existing capture URI
 * Mantine v8.x only.
 */

import React, { useState, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  ThemeIcon,
  Button,
  Alert,
  Loader,
  Center,
  SimpleGrid,
  Badge,
  Checkbox,
  Box,
  Tooltip,
} from '@mantine/core';
import {
  IconDeviceMobileVibration,
  IconCamera,
  IconMicrophone,
  IconQrcode,
  IconVideo,
  IconAlertCircle,
  IconCheck,
  IconRefresh,
  IconCloudUpload,
} from '@tabler/icons-react';
import { useWearableCaptures, type WearableCaptureRecord } from './hooks/useWearableCaptures';
import { useImportWearableCapture } from './hooks/useImportWearableCapture';
import type { UploadResult } from './hooks/useChaosDocumentUpload';

// ============================================================
// Types
// ============================================================

export interface WearablesCaptureImporterProps {
  onImportComplete?: (results: UploadResult[]) => void;
  onError?: (error: Error) => void;
}

// ============================================================
// Helpers
// ============================================================

function getCaptureIcon(type: WearableCaptureRecord['captureType']): React.ReactNode {
  switch (type) {
    case 'PHOTO':
      return <IconCamera size={16} />;
    case 'VOICE_NOTE':
      return <IconMicrophone size={16} />;
    case 'VIDEO':
      return <IconVideo size={16} />;
    case 'BARCODE_SCAN':
      return <IconQrcode size={16} />;
    default:
      return <IconCamera size={16} />;
  }
}

function getCaptureColor(type: WearableCaptureRecord['captureType']): string {
  switch (type) {
    case 'PHOTO':
      return 'blue';
    case 'VOICE_NOTE':
      return 'red';
    case 'VIDEO':
      return 'violet';
    case 'BARCODE_SCAN':
      return 'orange';
    default:
      return 'gray';
  }
}

function formatCaptureLabel(type: WearableCaptureRecord['captureType']): string {
  switch (type) {
    case 'PHOTO':
      return 'Photo';
    case 'VOICE_NOTE':
      return 'Voice Note';
    case 'VIDEO':
      return 'Video';
    case 'BARCODE_SCAN':
      return 'Barcode';
    default:
      return type;
  }
}

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}

// ============================================================
// CaptureCard sub-component
// ============================================================

interface CaptureCardProps {
  capture: WearableCaptureRecord;
  isSelected: boolean;
  importStatus: 'pending' | 'importing' | 'done' | 'error' | undefined;
  onToggle: (id: string) => void;
}

const CaptureCard: React.FC<CaptureCardProps> = ({ capture, isSelected, importStatus, onToggle }) => {
  const color = getCaptureColor(capture.captureType);
  const isDone = importStatus === 'done';
  const isImporting = importStatus === 'importing';
  const hasError = importStatus === 'error';

  return (
    <Card
      withBorder
      p="sm"
      onClick={() => !isDone && !isImporting && onToggle(capture.id)}
      style={{
        cursor: isDone || isImporting ? 'default' : 'pointer',
        borderColor: isSelected
          ? 'var(--mantine-color-blue-6)'
          : isDone
            ? 'var(--mantine-color-green-6)'
            : hasError
              ? 'var(--mantine-color-red-4)'
              : undefined,
        borderWidth: isSelected || isDone || hasError ? 2 : 1,
        opacity: isImporting ? 0.7 : 1,
        transition: 'border-color 0.15s ease',
      }}
    >
      <Stack gap={6}>
        <Group justify="space-between" align="flex-start">
          <ThemeIcon variant="light" size="sm" color={color}>
            {isDone ? <IconCheck size={14} /> : getCaptureIcon(capture.captureType)}
          </ThemeIcon>
          <Group gap={4}>
            {!isDone && !isImporting && (
              <Checkbox
                checked={isSelected}
                onChange={() => onToggle(capture.id)}
                size="xs"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {isImporting && <Loader size={14} />}
            {isDone && (
              <Badge size="xs" color="green" variant="filled">
                Imported
              </Badge>
            )}
            {hasError && (
              <Badge size="xs" color="red" variant="light">
                Failed
              </Badge>
            )}
          </Group>
        </Group>

        <Badge size="xs" color={color} variant="light" w="fit-content">
          {formatCaptureLabel(capture.captureType)}
        </Badge>

        {capture.transcription && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            "{capture.transcription}"
          </Text>
        )}

        {capture.barcodeValue && (
          <Text size="xs" ff="monospace" c="dimmed" lineClamp={1}>
            {capture.barcodeValue}
          </Text>
        )}

        {capture.contentUrl && capture.captureType === 'PHOTO' && (
          <Box
            style={{
              borderRadius: 4,
              overflow: 'hidden',
              height: 60,
              background: 'var(--mantine-color-gray-1)',
            }}
          >
            <img
              src={capture.contentUrl}
              alt="Wearable capture"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          </Box>
        )}

        <Text size="xs" c="dimmed">
          {formatRelativeTime(capture.capturedAt)}
        </Text>
      </Stack>
    </Card>
  );
};

// ============================================================
// Main component
// ============================================================

export const WearablesCaptureImporter: React.FC<WearablesCaptureImporterProps> = ({
  onImportComplete,
  onError,
}) => {
  const { captures, loading, error, refetch } = useWearableCaptures('UNIMPORTED');
  const { importCaptures, isImporting, importProgress, results, reset: resetImport } = useImportWearableCapture();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [phase, setPhase] = useState<'BROWSING' | 'IMPORTED'>('BROWSING');

  const toggleCapture = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleImport = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      const importResults = await importCaptures(ids);

      // Build UploadResult-compatible objects for documents that succeeded
      const uploadResults: UploadResult[] = importResults
        .filter((r) => r.documentId !== null)
        .map((r) => ({
          document: {
            documentId: r.documentId!,
            tenant: '',
            filename: `wearable-capture-${r.captureId}`,
            mimeType: 'image/jpeg',
            fileSize: 0,
            status: 'UPLOADED' as const,
            uploadedAt: new Date().toISOString(),
            uploadedBy: '',
            uploadUrl: null,
            expiresAt: null,
            intakeTarget: 'DOCUMENT',
          },
          uploadDurationMs: 0,
          s3Key: '',
        }));

      const failedCount = importResults.filter((r) => r.error !== null).length;

      if (uploadResults.length > 0) {
        setPhase('IMPORTED');
        setSelectedIds(new Set());
        onImportComplete?.(uploadResults);
      }

      if (failedCount > 0 && failedCount === importResults.length) {
        onError?.(new Error(`All ${failedCount} imports failed`));
      }
    } catch (err) {
      const importErr = err instanceof Error ? err : new Error('Import failed');
      onError?.(importErr);
    }
  }, [selectedIds, importCaptures, onImportComplete, onError]);

  const handleReset = useCallback(() => {
    resetImport();
    setSelectedIds(new Set());
    setPhase('BROWSING');
    refetch();
  }, [resetImport, refetch]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="violet">
            <IconDeviceMobileVibration size={20} />
          </ThemeIcon>
          <div>
            <Title order={4}>Wearables — Vuzix Captures</Title>
            <Text size="sm" c="dimmed">
              Import photos, voice notes, and scans captured via your Vuzix glasses
            </Text>
          </div>
          {captures.length > 0 && phase === 'BROWSING' && (
            <Badge ml="auto" color="blue" variant="light">
              {captures.length} unimported
            </Badge>
          )}
        </Group>

        {/* LOADING */}
        {loading && (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        )}

        {/* QUERY_ERROR */}
        {error && !loading && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Failed to Load Captures">
            <Group justify="space-between">
              <Text size="sm">{error.message}</Text>
              <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={refetch}>
                Retry
              </Button>
            </Group>
          </Alert>
        )}

        {/* EMPTY */}
        {!loading && !error && captures.length === 0 && phase === 'BROWSING' && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                <IconDeviceMobileVibration size={30} />
              </ThemeIcon>
              <Text size="lg" fw={500}>No captures to import</Text>
              <Text size="sm" c="dimmed" ta="center">
                Captures from your Vuzix glasses will appear here.
                Start a session on your device to begin capturing.
              </Text>
              <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={refetch}>
                Refresh
              </Button>
            </Stack>
          </Center>
        )}

        {/* BROWSING — capture grid */}
        {!loading && !error && captures.length > 0 && phase === 'BROWSING' && (
          <>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
              {captures.map((capture) => (
                <CaptureCard
                  key={capture.id}
                  capture={capture}
                  isSelected={selectedIds.has(capture.id)}
                  importStatus={importProgress.get(capture.id)}
                  onToggle={toggleCapture}
                />
              ))}
            </SimpleGrid>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {selectedIds.size > 0
                  ? `${selectedIds.size} capture${selectedIds.size !== 1 ? 's' : ''} selected`
                  : 'Click captures to select them'}
              </Text>
              <Group gap="xs">
                {selectedIds.size > 0 && (
                  <Button
                    size="sm"
                    variant="subtle"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear
                  </Button>
                )}
                <Tooltip
                  label={selectedIds.size === 0 ? 'Select at least one capture' : ''}
                  disabled={selectedIds.size > 0}
                >
                  <Button
                    leftSection={<IconCloudUpload size={16} />}
                    onClick={handleImport}
                    loading={isImporting}
                    disabled={selectedIds.size === 0}
                  >
                    Import Selected ({selectedIds.size})
                  </Button>
                </Tooltip>
              </Group>
            </Group>
          </>
        )}

        {/* IMPORTED summary */}
        {phase === 'IMPORTED' && (
          <Alert color="green" icon={<IconCheck size={16} />} title="Import Complete">
            <Stack gap="xs">
              <Text size="sm">
                {results.filter((r) => r.documentId).length} capture(s) imported into Document Studio.
                {results.filter((r) => r.error).length > 0 && (
                  <Text span c="orange">
                    {' '}{results.filter((r) => r.error).length} failed.
                  </Text>
                )}
              </Text>
              <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={handleReset}>
                Import More
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Card>
  );
};
